// Página pública — Diagnóstico Saúde e Emoções
// Rota: /vivencias/:token
// Fluxo guiado da Jornada das 12 Emoções: boas-vindas, pergunta da vivência
// atual, processamento por IA e exibição da devolutiva personalizada.

import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Sparkles, Lock, Loader2, Send, CheckCircle2, ChevronDown, ChevronUp} from 'lucide-react'
import { getVivencia } from '@/data/vivencias'

const PROCESSAR_URL = 'https://api.lumi.new/v1/functions/p427216140118241280/processar-vivencia'

interface ClienteInfo {
  _id: string
  nome: string
  vivenciaAtual: number
  vivenciasConcluidas?: number[]
  status: string
  dataInicio?: string
  dataExpiracao?: string
}

interface JobProcessamento {
  _id?: string
  token?: string
  numeroVivencia?: number
  status?: string
  relatorioJson?: string
  erroMensagem?: string
  finalizouTudo?: boolean
}

type Fase = 'carregando' | 'invalido' | 'boas_vindas' | 'apresentacao' | 'pergunta' | 'processando' | 'resultado' | 'concluido' | 'expirado'

interface RespostaPergunta {
  pergunta: string
  reconheco: string
  agradeco: string
  sou: string
}

interface PerguntaComOrientacao extends RespostaPergunta {
  orientacao: string
}

interface RelatorioVivencia {
  saudacao: string
  perguntas: PerguntaComOrientacao[]
  sequenciaLiberacao: string
  apoioEmocional: string
}

type TrechoOrientacao = 'reconhecimento' | 'convite' | 'reconheço' | 'agradeço' | 'sou'

interface ParagrafoOrientacao {
  tipo: TrechoOrientacao
  texto: string
}

const MARCA_FRASE_EU = /(?:Seu\s+)?EU\s+(?:RECONHE[CÇ]O|AGRADE[CÇ]O|SOU)\b/i

function ehTrechoReconheco(texto: string): boolean {
  return /\beu reconhe[cç]o\b/i.test(texto)
}

function ehTrechoAgradeco(texto: string): boolean {
  return /\beu agrade[cç]o\b/i.test(texto)
}

function ehTrechoSou(texto: string): boolean {
  if (ehTrechoAgradeco(texto) || ehTrechoReconheco(texto)) return false
  return /\beu sou\b/i.test(texto)
}

function ehFraseEu(texto: string): boolean {
  return ehTrechoReconheco(texto) || ehTrechoAgradeco(texto) || ehTrechoSou(texto)
}

function ehTrechoConvite(texto: string): boolean {
  return /\b(mais fundo|aprofund|convide|convidar|se quiser|quando quiser|ir al[eé]m|ir mais|poderia aprofund|vale ir|permita-se)\b/i.test(texto)
}

function classificarTrechoOrientacao(texto: string): TrechoOrientacao {
  if (ehTrechoAgradeco(texto)) return 'agradeço'
  if (ehTrechoReconheco(texto)) return 'reconheço'
  if (ehTrechoSou(texto)) return 'sou'
  if (ehTrechoConvite(texto)) return 'convite'
  return 'reconhecimento'
}

function separarSentencasOrientacao(texto: string): string[] {
  const partes = String(texto || '').match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g)
  if (!partes) return []
  return partes.map((parte) => parte.trim()).filter(Boolean)
}

function fatiarPorFrasesEu(bloco: string): string[] {
  const partes: string[] = []
  const marca = new RegExp(MARCA_FRASE_EU.source, 'gi')
  let ultimoFim = 0
  let match = marca.exec(bloco)
  while (match) {
    if (match.index > ultimoFim) {
      const antes = bloco.slice(ultimoFim, match.index).trim()
      if (antes) partes.push(antes)
      ultimoFim = match.index
    }
    match = marca.exec(bloco)
  }
  const resto = bloco.slice(ultimoFim).trim()
  if (resto) partes.push(resto)
  return partes
}

function dividirOrientacao(texto: string): ParagrafoOrientacao[] {
  const bruto = String(texto || '').replace(/\r\n/g, '\n').trim()
  if (!bruto) return []

  const blocos = bruto.split(/\n+/).map((bloco) => bloco.trim()).filter(Boolean)
  const pedacos: string[] = []

  for (const bloco of blocos) {
    pedacos.push(...fatiarPorFrasesEu(bloco))
  }

  const sentencas: string[] = []
  for (const pedaco of pedacos) {
    if (ehFraseEu(pedaco)) {
      sentencas.push(pedaco)
      continue
    }
    const recortes = separarSentencasOrientacao(pedaco)
    if (recortes.length > 0) {
      sentencas.push(...recortes)
    } else {
      sentencas.push(pedaco)
    }
  }

  const paragrafos: ParagrafoOrientacao[] = []
  for (const sentenca of sentencas) {
    const tipo = classificarTrechoOrientacao(sentenca)
    const anterior = paragrafos[paragrafos.length - 1]
    if (anterior && anterior.tipo === tipo && (tipo === 'reconhecimento' || tipo === 'convite')) {
      anterior.texto = `${anterior.texto} ${sentenca}`
    } else {
      paragrafos.push({ tipo, texto: sentenca })
    }
  }

  return paragrafos
}

function BlocoOrientacao({ texto }: { texto: string }) {
  const paragrafos = dividirOrientacao(texto)
  const conteudo = paragrafos.length > 0 ? paragrafos : [{ tipo: 'reconhecimento' as const, texto: String(texto || '').trim() }]

  return (
    <div>
      <p className="text-sm uppercase tracking-wide text-[#B08968] font-bold mb-5">Orientação:</p>
      <div className="space-y-4">
        {conteudo.map((paragrafo, i) => {
          const destaque = paragrafo.tipo === 'reconheço' || paragrafo.tipo === 'agradeço' || paragrafo.tipo === 'sou'
          return (
            <p
              key={`${paragrafo.tipo}-${i}`}
              className={destaque
                ? 'text-[#2D6A4F] text-lg italic font-medium leading-loose'
                : 'text-[#2C2C2A] text-lg leading-loose'}
            >
              {paragrafo.texto}
            </p>
          )
        })}
      </div>
    </div>
  )
}

export default function VivenciaCliente() {
  const { token } = useParams<{ token: string }>()
  const [cliente, setCliente] = useState<ClienteInfo | null>(null)
  const [fase, setFase] = useState<Fase>('carregando')
  const [perguntaIndex, setPerguntaIndex] = useState(0)
  const [respostas, setRespostas] = useState<RespostaPergunta[]>([])
  const [reconheco, setReconheco] = useState('')
  const [agradeco, setAgradeco] = useState('')
  const [sou, setSou] = useState('')
  const [relatorio, setRelatorio] = useState<RelatorioVivencia | null>(null)
  const [erro, setErro] = useState('')
  const [lembreteAberto, setLembreteAberto] = useState(false)
  const [mensagemProcessando, setMensagemProcessando] = useState('Gerando sua orientação...')

  useEffect(() => {
    document.title = 'Jornada das 12 Emoções — Diagnóstico Saúde e Emoções'
  }, [])

  // Proteção básica de conteúdo: bloqueia clique direito, seleção e Ctrl+C
  useEffect(() => {
    const bloquearContexto = (e: MouseEvent) => e.preventDefault()
    const bloquearSelecao = (e: Event) => e.preventDefault()
    const bloquearCopia = (e: ClipboardEvent) => e.preventDefault()
    document.addEventListener('contextmenu', bloquearContexto)
    document.addEventListener('selectstart', bloquearSelecao)
    document.addEventListener('copy', bloquearCopia)
    return () => {
      document.removeEventListener('contextmenu', bloquearContexto)
      document.removeEventListener('selectstart', bloquearSelecao)
      document.removeEventListener('copy', bloquearCopia)
    }
  }, [])

  const aplicarJob = useCallback((job?: JobProcessamento | null) => {
    if (!job) return false
    if (job.status === 'concluido' && job.relatorioJson) {
      try {
        const parsed = JSON.parse(job.relatorioJson)
        if (parsed?.saudacao) {
          setRelatorio(parsed)
          setFase('resultado')
          return true
        }
      } catch {
        return false
      }
    }
    if (job.status === 'pendente' || job.status === 'processando') {
      setFase('processando')
      return true
    }
    if (job.status === 'erro') {
      setErro(job.erroMensagem || 'Não foi possível processar sua vivência agora. Tente novamente.')
      return false
    }
    return false
  }, [])

  const buscarJobAtual = useCallback(async (numeroVivencia: number) => {
    if (!token) return null
    const res = await lumi.entities.vivencias_processamento.list({
      filter: { token, numeroVivencia },
      sort: { createdAt: -1 },
      limit: 1,
    })
    return (res?.list?.[0] as JobProcessamento | undefined) || null
  }, [token])

  const dispararWorker = useCallback(async (numeroVivencia?: number) => {
    try {
      await fetch(PROCESSAR_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          acao: 'run',
          token,
          ...(numeroVivencia ? { numeroVivencia } : {}),
        }),
      })
    } catch {
      // O cron a cada minuto processa mesmo se este POST for cortado.
    }
  }, [token])

  const carregarCliente = useCallback(async () => {
    if (!token) {
      setFase('invalido')
      return
    }
    try {
      const res = await lumi.entities.vivencias_clientes.list({ filter: { token }, limit: 1 })
      const c = res?.list?.[0]
      if (!c) {
        setFase('invalido')
        return
      }
      const atual = Math.max(1, Number(c.vivenciaAtual) || 1)
      const clienteNormalizado = { ...c, vivenciaAtual: atual }
      const jaIniciou = Boolean(String(c.dataInicio || '').trim())
      if (c.status === 'ativo' && !jaIniciou && c._id) {
        const agoraPrimeiroAcesso = new Date().toISOString()
        const dataExpiracao = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        try {
          await lumi.entities.vivencias_clientes.update(c._id, {
            dataInicio: agoraPrimeiroAcesso,
            dataExpiracao,
            updatedAt: agoraPrimeiroAcesso,
          })
          clienteNormalizado.dataInicio = agoraPrimeiroAcesso
          clienteNormalizado.dataExpiracao = dataExpiracao
        } catch (e) {
          console.error('[vivencias] erro ao registrar primeiro acesso', e)
        }
      }
      if (c.status === 'expirado' || c.status === 'inativo') {
        setCliente(clienteNormalizado)
        setFase('expirado')
        return
      }
      if (c.status === 'concluido' || atual > 12) {
        setCliente(clienteNormalizado)
        setFase('concluido')
        return
      }
      setCliente(clienteNormalizado)
      const concluidas = Array.isArray(c.vivenciasConcluidas) ? c.vivenciasConcluidas.map(Number) : []
      if (concluidas.includes(atual) && atual < 12) {
        const proxima = atual + 1
        const agora = new Date().toISOString()
        try {
          await lumi.entities.vivencias_clientes.update(c._id, {
            vivenciaAtual: proxima,
            dataLiberacaoAtual: agora,
            enviouLembreteReengajamento24h: false,
            updatedAt: agora,
          })
        } catch (e) {
          console.error('[vivencias] erro ao liberar próxima vivência', e)
        }
        setCliente({ ...clienteNormalizado, vivenciaAtual: proxima })
        setRelatorio(null)
        setPerguntaIndex(0)
        setRespostas([])
        setFase('boas_vindas')
        return
      }
      const job = await buscarJobAtual(atual)
      if (aplicarJob(job)) return
      setFase('boas_vindas')
    } catch (e) {
      console.error('[vivencias] erro ao carregar cliente', e)
      setFase('invalido')
    }
  }, [token, aplicarJob, buscarJobAtual])

  useEffect(() => {
    void carregarCliente()
    void fetch(PROCESSAR_URL, { method: 'GET', cache: 'no-store' }).catch(() => {})
  }, [carregarCliente])

  useEffect(() => {
    if (fase !== 'processando' || !cliente) return
    let ativo = true
    const numero = Number(cliente.vivenciaAtual)
    const consultar = async () => {
      try {
        const job = await buscarJobAtual(numero)
        if (!ativo) return
        if (job?.status === 'concluido' && job.relatorioJson) {
          aplicarJob(job)
          return
        }
        if (job?.status === 'erro') {
          setErro(job.erroMensagem || 'Não foi possível processar sua vivência agora. Tente novamente.')
          setFase('pergunta')
        }
      } catch (e) {
        console.error('[vivencias] erro ao consultar processamento', e)
      }
    }
    void consultar()
    void dispararWorker(numero)
    const timer = window.setInterval(() => {
      void consultar()
    }, 1500)
    const kick = window.setInterval(() => {
      void dispararWorker(numero)
    }, 8000)
    return () => {
      ativo = false
      window.clearInterval(timer)
      window.clearInterval(kick)
    }
  }, [fase, cliente, buscarJobAtual, aplicarJob, dispararWorker])

  useEffect(() => {
    if (fase !== 'processando') {
      setMensagemProcessando('Gerando sua orientação...')
      return
    }
    const mensagens = [
      'Gerando sua orientação...',
      'Lendo suas respostas com presença...',
      'Preparando sua reflexão personalizada...',
      'Quase lá, estamos finalizando...',
    ]
    let indice = 0
    const timer = window.setInterval(() => {
      indice = (indice + 1) % mensagens.length
      setMensagemProcessando(mensagens[indice])
    }, 4000)
    return () => window.clearInterval(timer)
  }, [fase])

  const vivencia = cliente ? getVivencia(Number(cliente.vivenciaAtual)) : undefined
  const perguntaAtual = vivencia?.perguntas?.[perguntaIndex]
  const camposPreenchidos = reconheco.trim().length >= 3 && agradeco.trim().length >= 3 && sou.trim().length >= 3

  const diasRestantes = (() => {
    if (!cliente) return null
    let referencia: Date | null = null
    if (cliente.dataExpiracao) {
      referencia = new Date(cliente.dataExpiracao)
    } else if (cliente.dataInicio) {
      const inicio = new Date(cliente.dataInicio)
      referencia = new Date(inicio.getTime() + 30 * 24 * 60 * 60 * 1000)
    }
    if (!referencia || isNaN(referencia.getTime())) return null
    const diffMs = referencia.getTime() - Date.now()
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
  })()

  const handleVerApresentacao = () => {
    setFase('apresentacao')
  }

  const handleIniciarPerguntas = () => {
    setPerguntaIndex(0)
    setRespostas([])
    setReconheco('')
    setAgradeco('')
    setSou('')
    setLembreteAberto(false)
    setFase('pergunta')
  }

  const handleProximaPergunta = async () => {
    if (!vivencia || !perguntaAtual || !camposPreenchidos) return
    const novaResposta: RespostaPergunta = {
      pergunta: perguntaAtual.pergunta,
      reconheco: reconheco.trim(),
      agradeco: agradeco.trim(),
      sou: sou.trim(),
    }
    const todasRespostas = [...respostas.slice(0, perguntaIndex), novaResposta]

    if (perguntaIndex < vivencia.perguntas.length - 1) {
      setRespostas(todasRespostas)
      setPerguntaIndex(perguntaIndex + 1)
      setReconheco('')
      setAgradeco('')
      setSou('')
      setLembreteAberto(false)
      return
    }

    // Última pergunta: envia tudo para processamento
    setRespostas(todasRespostas)
    await handleEnviar(todasRespostas)
  }

  const handleEnviar = async (todasRespostas: RespostaPergunta[]) => {
    if (!cliente || !vivencia || !token) return
    setFase('processando')
    setErro('')
    try {
      const existente = await buscarJobAtual(vivencia.numero)
      if (existente && (existente.status === 'pendente' || existente.status === 'processando' || existente.status === 'concluido')) {
        aplicarJob(existente)
        void dispararWorker(vivencia.numero)
        return
      }
      const agora = new Date().toISOString()
      const payload = {
        token,
        numeroVivencia: vivencia.numero,
        status: 'pendente',
        respostasJson: JSON.stringify(todasRespostas.filter((item, indice, lista) =>
          lista.findIndex((outra) => outra.pergunta === item.pergunta) === indice
        )),
        oleo: vivencia.oleo,
        titulo: vivencia.titulo,
        tema: vivencia.tema,
        indicadoPara: vivencia.indicadoPara,
        orientacaoPratica: vivencia.orientacaoPratica || '',
        indicacoesFisicasJson: JSON.stringify(vivencia.indicacoesFisicas || []),
        nomeCliente: cliente.nome,
        relatorioJson: '',
        erroMensagem: '',
        erroDetalhe: '',
        tentativas: 0,
        processandoDesde: '',
        finalizouTudo: false,
        creator: 'vivencia-cliente',
        createdAt: agora,
        updatedAt: agora,
      }
      if (existente?._id && existente.status === 'erro') {
        await lumi.entities.vivencias_processamento.update(existente._id, payload)
      } else {
        await lumi.entities.vivencias_processamento.create(payload)
      }
      void dispararWorker(vivencia.numero)
    } catch (e) {
      console.error('[vivencias] erro ao enfileirar', e)
      setErro('Não foi possível processar sua vivência agora. Tente novamente.')
      setFase('pergunta')
    }
  }

  // ── Telas de estado ──────────────────────────────────────────────────────
  if (fase === 'carregando') {
    return (
      <TelaBase>
        <SeloJornada />
        <Loader2 className="w-8 h-8 text-[#2D6A4F] animate-spin mx-auto mb-3" />
        <p className="text-[#5F5E5A] text-lg">Carregando sua vivência...</p>
      </TelaBase>
    )
  }

  if (fase === 'invalido') {
    return (
      <TelaBase>
        <SeloJornada />
        <Lock className="w-10 h-10 text-[#B08968] mx-auto mb-4" />
        <h1 className="font-cormorant text-3xl font-semibold text-[#2C2C2A] mb-3">Link inválido</h1>
        <p className="text-[#5F5E5A] text-lg leading-relaxed">
          Este link da Jornada das 12 Emoções não foi encontrado. Verifique com quem enviou este acesso.
        </p>
      </TelaBase>
    )
  }

  if (fase === 'expirado') {
    return (
      <TelaBase>
        <SeloJornada />
        <Lock className="w-10 h-10 text-[#B08968] mx-auto mb-4" />
        <h1 className="font-cormorant text-3xl font-semibold text-[#2C2C2A] mb-3">Acesso expirado</h1>
        <p className="text-[#5F5E5A] text-lg leading-relaxed mb-3">
          Sua Jornada das 12 Emoções tinha um prazo de 30 dias para ser finalizada, mas você não conseguiu concluir e o seu acesso expirou.
        </p>
        <p className="text-[#5F5E5A] text-lg leading-relaxed mb-6">
          Se desejar recomeçar essa experiência de liberação emocional e elevação de vibração você pode adquirir um novo acesso.
        </p>
        <a
          href="https://pay.hotmart.com/T107316970V"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center bg-[#2D6A4F] text-white px-6 py-3 rounded-full font-medium text-lg hover:bg-opacity-90 transition-colors"
        >
          Quero recomeçar minha jornada →
        </a>
      </TelaBase>
    )
  }

  if (fase === 'concluido') {
    return (
      <TelaBase>
        <SeloJornada />
        <div className="w-14 h-14 rounded-full bg-[#D8F3DC] flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7 text-[#2D6A4F]" />
        </div>
        <h1 className="font-cormorant text-3xl font-semibold text-[#2C2C2A] mb-3">
          Você concluiu a Jornada das 12 Emoções, {cliente?.nome?.split(' ')[0]}! 🌿
        </h1>
        <p className="text-[#5F5E5A] text-lg leading-relaxed">
          Parabéns por ter percorrido essa jornada de autoconhecimento até o fim. Que os aprendizados e reflexões continuem florescendo no seu dia a dia.
        </p>
      </TelaBase>
    )
  }

  if (fase === 'boas_vindas' && cliente && Number(cliente.vivenciaAtual) <= 1) {
    return (
      <TelaBase wide>
        <SeloJornada />
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-[#D8F3DC] flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-[#2D6A4F]" />
          </div>
          <p className="text-sm sm:text-base uppercase tracking-wide text-[#B08968] font-bold mb-2">Vivência {cliente.vivenciaAtual} de 12</p>
          <h1 className="font-cormorant text-3xl sm:text-4xl font-semibold text-[#2C2C2A] mb-4">
            Olá, {cliente.nome.split(' ')[0]}! 🌿
          </h1>
        </div>
        <div className="text-[#5F5E5A] text-lg leading-relaxed space-y-4 text-left">
          <p>
            Você recebeu o acesso à Jornada das 12 Emoções com a Metodologia de Mônica Maria, Terapeuta Vibracional e Aromaterapeuta.
          </p>
          <p>
            Assim que você conclui uma vivência, a próxima fica disponível imediatamente. Você pode avançar quantas quiser no mesmo dia, ou retomar em outro momento.
          </p>
          <p>
            Você tem 30 dias a partir de hoje para concluir todas as vivências. Após esse prazo o acesso será encerrado automaticamente.
          </p>
          <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-4">
            <p className="text-[#2D6A4F] text-lg">
              🔒 Suas respostas são completamente confidenciais. Elas são processadas apenas para gerar seu relatório personalizado e não são armazenadas em nenhum banco de dados. Nem a consultora que liberou seu acesso, nem a administração do sistema têm acesso ao conteúdo do que você escrever. Apenas você receberá o relatório, diretamente no seu e-mail.
            </p>
          </div>
          <p>
            Cada vivência envolve um óleo essencial diferente. Ao longo da Jornada das 12 Emoções você passará por um processo completo de autoconhecimento e transformação interior.
          </p>
          <p>
            Cada óleo essencial tem uma linguagem emocional própria e te convida a trabalhar um aspecto emocional central. As perguntas de cada vivência foram criadas especificamente para o campo emocional que aquele óleo promove, aprofundando o processo de liberação e elevação interior.
          </p>
          <p>
            Responda cada vivência com presença, honestidade e sem pressa. Quanto mais profundo você for nas respostas, mais transformadora será a experiência.
          </p>
        </div>
        <div className="text-center mt-6">
          <button
            onClick={handleVerApresentacao}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white px-6 py-3 rounded-full font-medium text-lg hover:bg-opacity-90 transition-colors"
          >
            Começar minha Vivência {cliente.vivenciaAtual} →
          </button>
        </div>
      </TelaBase>
    )
  }

  if (fase === 'boas_vindas' && cliente && Number(cliente.vivenciaAtual) > 1) {
    return (
      <TelaBase wide>
        <SeloJornada />
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-[#D8F3DC] flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-[#2D6A4F]" />
          </div>
          <p className="text-sm sm:text-base uppercase tracking-wide text-[#B08968] font-bold mb-2">Vivência {cliente.vivenciaAtual} de 12</p>
          <h1 className="font-cormorant text-3xl sm:text-4xl font-semibold text-[#2C2C2A] mb-4">
            Olá, {cliente.nome.split(' ')[0]}! 🌿
          </h1>
          <p className="text-[#5F5E5A] text-lg leading-relaxed">
            Que bom ter você aqui para mais uma transformação.
          </p>
        </div>
        <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-5 mt-6 space-y-3 text-left">
          <p className="text-[#2D6A4F] text-base leading-relaxed">
            → Suas respostas são completamente confidenciais
          </p>
          <p className="text-[#2D6A4F] text-base leading-relaxed">
            → Responda com presença, honestidade e sem pressa
          </p>
          <p className="text-[#2D6A4F] text-base leading-relaxed">
            → Seu relatório chegará no seu e-mail após o envio
          </p>
          {diasRestantes !== null && (
            <p className="text-[#2D6A4F] text-base leading-relaxed">
              → Você tem {diasRestantes} {diasRestantes === 1 ? 'dia restante' : 'dias restantes'} para concluir sua jornada
            </p>
          )}
        </div>
        <div className="text-center mt-6">
          <button
            onClick={handleVerApresentacao}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white px-6 py-3 rounded-full font-medium text-lg hover:bg-opacity-90 transition-colors"
          >
            Começar Vivência {cliente.vivenciaAtual} →
          </button>
        </div>
      </TelaBase>
    )
  }

  if (fase === 'apresentacao' && cliente && vivencia) {
    return (
      <TelaBase wide>
        <SeloJornada />
        {/* Bloco 1 — Identificação */}
        <div className="text-center mb-6">
          <span className="text-4xl">{vivencia.emoji}</span>
          <p className="text-base sm:text-lg uppercase tracking-wide text-[#B08968] font-bold mt-2">
            Vivência {vivencia.numero} de 12
          </p>
        </div>

        {/* Bloco 2 — Objetivo da vivência */}
        <div className="bg-white border-2 border-[#EDE0D4] rounded-2xl p-5 mb-5">
          <h2 className="font-cormorant text-xl sm:text-2xl font-bold text-[#2D6A4F] mb-3">O objetivo da vivência:</h2>
          <p className="text-[#2C2C2A] text-lg leading-relaxed first-letter:uppercase">{vivencia.tema}.</p>
        </div>

        {/* Bloco 3 — Óleo Essencial da vivência */}
        <div className="bg-[#D8F3DC] rounded-2xl p-5 mb-5 text-center">
          <h2 className="font-cormorant text-xl sm:text-2xl font-bold text-[#2D6A4F] mb-3">Óleo Essencial da vivência:</h2>
          <span className="text-4xl">{vivencia.emoji}</span>
          <p className="text-[#2C2C2A] text-lg leading-relaxed mt-2">
            <span className="font-semibold">{vivencia.oleo}</span> — É o óleo essencial de {vivencia.titulo.toLowerCase()}.
          </p>
        </div>

        {/* Bloco 4 — O que esse óleo trabalha emocionalmente */}
        <div className="bg-white border-2 border-[#EDE0D4] rounded-2xl p-5 mb-5">
          <h2 className="font-cormorant text-xl sm:text-2xl font-bold text-[#2D6A4F] mb-3">
            O que esse óleo essencial trabalha emocionalmente:
          </h2>
          <p className="font-bold text-[#B08968] text-base sm:text-lg mb-1">É indicado para:</p>
          <p className="text-[#2C2C2A] text-lg leading-relaxed mb-4">{vivencia.indicadoPara}</p>
          <p className="font-bold text-[#B08968] text-base sm:text-lg mb-1">Promove:</p>
          <p className="text-[#2C2C2A] text-lg leading-relaxed">{vivencia.promove}</p>
        </div>

        {/* Bloco 5 — Orientações para a vivência */}
        <div className="bg-[#F8F4EF] border-2 border-[#EDE0D4] rounded-2xl p-5 mb-5">
          <h2 className="font-cormorant text-xl sm:text-2xl font-bold text-[#2D6A4F] mb-3">Orientações para a vivência:</h2>
          <p className="text-[#2C2C2A] text-lg leading-relaxed">
            Você vai perceber ao longo das vivências que cada óleo essencial vai ser utilizado de uma forma específica. Siga totalmente as orientações. Utilizar os óleos antes de cada vivência vai totalmente lhe ajudar a acessar padrões que estão prontos para serem transformados.
          </p>
        </div>

        {/* Bloco 6 — Primeiro Passo */}
        <div className="bg-white border-2 border-[#EDE0D4] rounded-2xl p-5 mb-6">
          <h2 className="font-cormorant text-xl sm:text-2xl font-bold text-[#2D6A4F] mb-3">Primeiro Passo:</h2>
          <p className="text-[#2C2C2A] text-lg leading-relaxed">{vivencia.comoUsarAntes}</p>
        </div>

        {/* Bloco 7 — Botão */}
        <div className="text-center">
          <button
            onClick={handleIniciarPerguntas}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white px-6 py-3 rounded-full font-medium text-lg hover:bg-opacity-90 transition-colors"
          >
            Estou pronto para responder →
          </button>
        </div>
      </TelaBase>
    )
  }

  if (fase === 'processando' && cliente && vivencia) {
    return (
      <TelaBase>
        <SeloJornada />
        <Loader2 className="w-8 h-8 text-[#2D6A4F] animate-spin mx-auto mb-4" />
        <h1 className="font-cormorant text-3xl font-semibold text-[#2C2C2A] mb-3">
          {mensagemProcessando}
        </h1>
        <p className="text-[#5F5E5A] text-lg leading-relaxed">
          Estamos preparando o relatório da Vivência {vivencia.numero} com o {vivencia.oleo}. Costuma levar cerca de 15 a 30 segundos.
        </p>
      </TelaBase>
    )
  }

  if (fase === 'pergunta' && cliente && vivencia && perguntaAtual) {
    const ultimaPergunta = perguntaIndex >= vivencia.perguntas.length - 1
    return (
      <TelaBase wide>
        <SeloJornada />
        <div className="text-center mb-6">
          <span className="text-4xl">{vivencia.emoji}</span>
          <p className="text-sm sm:text-base uppercase tracking-wide text-[#B08968] font-bold mt-2">
            Vivência {vivencia.numero} de 12 · {vivencia.oleo} · Pergunta {perguntaIndex + 1} de {vivencia.perguntas.length}
          </p>
          <h1 className="font-cormorant text-3xl sm:text-4xl font-semibold text-[#2C2C2A] mt-1">{vivencia.titulo}</h1>
        </div>
        <div className="bg-[#D8F3DC] rounded-2xl p-5 mb-5">
          <p className="text-[#2D6A4F] font-semibold text-lg sm:text-xl text-center leading-relaxed">{perguntaAtual.pergunta}</p>
        </div>

        <button
          type="button"
          onClick={() => setLembreteAberto(true)}
          className="w-full flex items-start gap-2 text-left mb-2 text-[#C9A84C] font-bold text-sm sm:text-base leading-relaxed hover:underline"
        >
          <span>⚠️ Leia antes de responder. Clique aqui para ver as orientações de como responder.</span>
        </button>
        <button
          type="button"
          onClick={() => setLembreteAberto(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-[#F8F4EF] border-2 border-[#C9A84C] rounded-xl text-[#B08968] font-semibold text-base sm:text-lg mb-4"
        >
          <span>Como responder</span>
          {lembreteAberto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {lembreteAberto && (
          <div className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl p-4 mb-5 space-y-4 text-base text-[#5F5E5A]">
            <div>
              <p className="font-semibold text-[#B08968] mb-1">"Eu reconheço que..."</p>
              <p className="mb-1">Traga a dor real, o padrão, o sentimento que de fato existe. Seja específico e profundo.</p>
              <p className="italic">Exemplo: "Eu reconheço que sinto muita raiva, decepção e tristeza dessa briga que tive com minha amiga."</p>
            </div>
            <div>
              <p className="font-semibold text-[#B08968] mb-1">"Eu agradeço por..."</p>
              <p className="mb-1">Saia do campo da dor e entre no campo da elevação. Escreva algo completamente positivo, sem mencionar o negativo.</p>
              <p className="italic">Exemplo: "Eu agradeço por sentir aceitação, calma e coragem para resolver e encarar essa situação com leveza."</p>
            </div>
            <div>
              <p className="font-semibold text-[#B08968] mb-1">"Eu sou..."</p>
              <p className="mb-1">Declare quem você é em sua essência mais profunda. Termine sempre com "Eu sou o Eu Sou."</p>
              <p className="italic">Exemplo: "Eu sou o amor que ajusta toda situação com consciência, verdade e calma."</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-base font-semibold text-[#B08968] mb-1">Eu reconheço que...</label>
            <textarea
              value={reconheco}
              onChange={e => setReconheco(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 text-base sm:text-lg border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none resize-none disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-base font-semibold text-[#B08968] mb-1">Eu agradeço por...</label>
            <textarea
              value={agradeco}
              onChange={e => setAgradeco(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 text-base sm:text-lg border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none resize-none disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-base font-semibold text-[#B08968] mb-1">Eu sou...</label>
            <textarea
              value={sou}
              onChange={e => setSou(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 text-base sm:text-lg border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none resize-none disabled:opacity-60"
            />
          </div>
        </div>

        {erro && <p className="text-red-600 text-base mt-2">{erro}</p>}
        <button
          onClick={handleProximaPergunta}
          disabled={!camposPreenchidos}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-6 py-3 rounded-full font-medium text-lg hover:bg-opacity-90 transition-colors disabled:opacity-60"
        >
          {ultimaPergunta ? (
            <><Send className="w-4 h-4" /> Enviar minhas respostas</>
          ) : (
            <>Próxima pergunta →</>
          )}
        </button>
      </TelaBase>
    )
  }

  if (fase === 'resultado' && cliente && vivencia && relatorio) {
    const ultimaVivencia = vivencia.numero >= 12
    return (
      <TelaBase wide>
        <SeloJornada />
        {/* Bloco 1 — Saudação e acolhimento */}
        <div className="text-center mb-6">
          <span className="text-4xl">{vivencia.emoji}</span>
          <h1 className="font-cormorant text-3xl sm:text-4xl font-semibold text-[#2C2C2A] mt-2">Sua reflexão de hoje</h1>
        </div>
        <div className="bg-white border border-[#EDE0D4] rounded-2xl p-6 shadow-sm mb-8">
          <p className="text-[#2C2C2A] text-lg leading-relaxed whitespace-pre-line">{relatorio.saudacao}</p>
        </div>

        {/* Bloco 2 — Cards de cada pergunta */}
        <div className="space-y-5 mb-8">
          {relatorio.perguntas.map((p, i) => (
            <div key={i} className="bg-white border-2 border-[#EDE0D4] rounded-2xl p-5">
              <p className="text-sm uppercase tracking-wide text-[#B08968] font-bold mb-2">Pergunta {i + 1}</p>
              <p className="text-[#2D6A4F] font-semibold text-lg leading-relaxed mb-4">{p.pergunta}</p>

              <p className="text-sm uppercase tracking-wide text-[#B08968] font-bold mb-2">Suas respostas:</p>
              <div className="bg-[#D8F3DC] rounded-xl p-4 mb-4 space-y-2">
                <p className="text-[#2C2C2A] text-base leading-relaxed"><span className="font-semibold">Eu reconheço que:</span> {p.reconheco}</p>
                <p className="text-[#2C2C2A] text-base leading-relaxed"><span className="font-semibold">Eu agradeço por:</span> {p.agradeco}</p>
                <p className="text-[#2C2C2A] text-base leading-relaxed"><span className="font-semibold">Eu sou:</span> {p.sou}</p>
              </div>

              <BlocoOrientacao texto={p.orientacao} />
            </div>
          ))}
        </div>

        {/* Bloco 3 — Sequência de liberação e elevação do coração */}
        <div className="mb-8">
          <h2 className="font-cormorant text-2xl font-semibold text-[#2D6A4F] mb-1">✦ Sua sequência para liberar e elevar seu coração</h2>
          <p className="text-[#5F5E5A] text-sm italic mb-4">Leia em voz alta agora. Sinta cada frase a partir de você. Permita que o alívio chegue.</p>
          <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-6">
            <p className="text-[#2C2C2A] text-lg leading-loose whitespace-pre-line">{relatorio.sequenciaLiberacao}</p>
          </div>
        </div>

        {/* Bloco 4 — Apoio emocional com o óleo */}
        <div className="mb-8">
          <h2 className="font-cormorant text-xl font-semibold text-[#2D6A4F] mb-2">✦ Seu apoio emocional com o {vivencia.oleo}</h2>
          <p className="text-[#2C2C2A] text-base leading-relaxed whitespace-pre-line">{relatorio.apoioEmocional}</p>
        </div>

        {/* Bloco 4.5 — Indicações físicas e dicas de uso do óleo */}
        <div className="mb-8">
          <h2 className="font-cormorant text-xl font-semibold text-[#2D6A4F] mb-4">✦ Indicações físicas e dicas de uso do {vivencia.oleo}</h2>
          <div className="space-y-4">
            {(vivencia.indicacoesFisicas || []).map((item, i) => (
              <div key={`${item.titulo}-${i}`} className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl p-4">
                <p className="font-semibold text-[#2D6A4F] text-base mb-1">🟢 {item.titulo}:</p>
                <p className="text-[#2C2C2A] text-base leading-relaxed">{item.texto}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bloco 5 — Próximo passo */}
        {ultimaVivencia ? (
          <div className="text-center">
            <p className="text-[#2D6A4F] font-medium text-lg">Você concluiu a Jornada das 12 Emoções! 🌿 Parabéns pela sua jornada.</p>
          </div>
        ) : (
          <div className="bg-[#FAEEDA] border border-[#EDD9B8] rounded-2xl p-5 text-center space-y-4">
            <p className="text-base text-[#854F0B] leading-relaxed">
              🌿 Seu relatório completo foi enviado para seu e-mail. Guarde-o e releia quando precisar.
            </p>
            <p className="text-sm text-[#854F0B]/90 leading-relaxed">
              Se preferir avançar para a próxima vivência, inspire e siga adiante. Você pode continuar em outro momento, para refletir e integrar essas novas percepções que sentiu agora. Guarde este link para retomar quando quiser.
            </p>
            <button
              type="button"
              onClick={() => {
                setRelatorio(null)
                setPerguntaIndex(0)
                setRespostas([])
                setReconheco('')
                setAgradeco('')
                setSou('')
                setErro('')
                void carregarCliente()
              }}
              className="inline-flex items-center justify-center bg-[#2D6A4F] text-white px-6 py-3 rounded-full font-medium text-lg hover:bg-opacity-90 transition-colors"
            >
              Avançar para a próxima vivência →
            </button>
          </div>
        )}
      </TelaBase>
    )
  }

  return (
    <TelaBase>
      <SeloJornada />
      <Loader2 className="w-8 h-8 text-[#2D6A4F] animate-spin mx-auto mb-3" />
      <p className="text-[#5F5E5A] text-lg">Carregando sua vivência...</p>
    </TelaBase>
  )
}

function SeloJornada() {
  return (
    <p className="text-center text-xs uppercase tracking-[0.18em] text-[#B08968] font-semibold mb-4">
      Jornada das 12 Emoções
    </p>
  )
}

function TelaBase({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4 py-10">
      <div className={`bg-white rounded-2xl shadow-md p-8 sm:p-10 text-center ${wide ? 'max-w-xl w-full text-left' : 'max-w-md w-full'}`}>
        {children}
      </div>
    </div>
  )
}
